import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the threshold backgroundPlugin globally
const thresholdBackgroundPlugin = {
    id: 'thresholdBackground',
    beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) return;
        
        const { top, bottom, left, right, width } = chartArea;
        const { y } = scales;
        
        if (!y) return;
        
        // Get thresholds and metric info from the chart's options
        const thresholds = chart.options.plugins.thresholds || {};
        const isRespiratoryChart = chart.options.plugins.isRespiratoryChart;
        const viewType = chart.options.plugins.viewType;
        
        // Only show background for respiratory rate in overall view
        if (!isRespiratoryChart || viewType !== 'overall' || !thresholds.low || !thresholds.normalLow || !thresholds.normalHigh || !thresholds.high) {
            // For non-respiratory or non-overall views, just fill with white background
            ctx.fillStyle = 'white';
            ctx.fillRect(left, top, width, bottom - top);
            return;
        }
        
        // Fill with white background first
        ctx.fillStyle = 'white';
        //ctx.fillRect(left, top, width, bottom - top);
        
        // We draw from bottom to top
        // Very Low Zone (Red) - from 0 to low
        const lowY = y.getPixelForValue(thresholds.low);
        ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 80% opacity
        //ctx.fillRect(left, lowY, width, bottom - lowY);
        
        // Low Zone (Yellow) - from low to normalLow
        const normalLowY = y.getPixelForValue(thresholds.normalLow);
        ctx.fillStyle = 'rgba(255, 229, 102, 0.4)'; // Yellow with 80% opacity
        //ctx.fillRect(left, normalLowY, width, lowY - normalLowY);
        
        // Normal Zone (Green) - from normalLow to normalHigh
        const normalHighY = y.getPixelForValue(thresholds.normalHigh);
        ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with 80% opacity
        //ctx.fillRect(left, normalHighY, width, normalLowY - normalHighY);
        
        // High Zone (Yellow) - from normalHigh to high
        const highY = y.getPixelForValue(thresholds.high);
        ctx.fillStyle = 'rgba(255, 229, 102, 0.4)'; // Yellow with 80% opacity
        //ctx.fillRect(left, highY, width, normalHighY - highY);
        
        // Very High Zone (Red) - from high to max
        ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 80% opacity
        //ctx.fillRect(left, top, width, highY - top);
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin,
    thresholdBackgroundPlugin
);

// Updated getBarColor function to handle zero values
const getBarColor = (value, thresholds, zeroValue = false) => {
    // If it's a zero value, we still want to show the threshold color it would have
    if (zeroValue) {
        return 'rgba(144, 255, 140, 0.5)'; // light green for zero values
    }
    
    if (value <= thresholds.low) return '#FF8A8A'; // Red (very low)
    if (value <= thresholds.normalLow) return '#FFE566'; // Yellow (low)
    if (value <= thresholds.normalHigh) return '#90FF8C'; // Green (normal)
    if (value <= thresholds.high) return '#FFE566'; // Yellow (high)
    return '#FF8A8A'; // Red (very high)
};

const ModularTrendsTimeSplitChart = ({ 
    title, 
    metricLabel, 
    overallData, 
    dayData, 
    nightData, 
    thresholds, 
    minValue, 
    maxValue,
    SettingResp = {}
}) => {
    // Add state for view type (overall, day, night)
    const [viewType, setViewType] = useState('overall');
    
    // Add effect hook to re-calculate thresholds when viewType changes
    useEffect(() => {
        // This will force re-calculation when view type changes
    }, [viewType]);
    
    // Format date for x-axis labels
    const formatDateLabel = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return '';
            }
            
            const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
            const monthDay = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            
            return `${weekday}\n${monthDay}`;
        } catch (error) {
            console.error('Date parsing error:', error);
            return '';
        }
    };

    // Process data for each time period (overall, day, night)
    const processedData = React.useMemo(() => {
        // Helper function to process a single data set
        const processDataSet = (dataSet) => {
            if (!Array.isArray(dataSet)) return { values: [], dates: [] };
            
            const processed = dataSet.reduce((acc, item) => {
                let value = typeof item === 'object' ? parseFloat(item.value) : parseFloat(item);
                let date = typeof item === 'object' ? item.period_start : null;
                
                // Handle time format like "8h 30m" for sleep data
                if (typeof item === 'object' && item.value && typeof item.value === 'string') {
                    const timeMatch = item.value.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                    if (timeMatch) {
                        // Convert hours and minutes to total minutes for the chart
                        const hours = parseInt(timeMatch[1], 10);
                        const minutes = parseInt(timeMatch[2], 10);
                        value = hours * 60 + minutes; // Convert to total minutes
                    }
                }
                
                acc.values.push(isNaN(value) ? 0 : value);
                acc.dates.push(date ? formatDateLabel(date) : '');
                
                return acc;
            }, { values: [], dates: [] });
            
            // Reverse arrays to show oldest to newest (left to right)
            return {
                values: processed.values.reverse(),
                dates: processed.dates.reverse()
            };
        };
        
        // Process all three data sets
        const overall = processDataSet(overallData);
        const day = processDataSet(dayData);
        const night = processDataSet(nightData);
        
        // For sleep data, we might want to calculate overall as the sum of day and night
        // For respiratory rate, we use the provided overall value directly
        const isSleepData = metricLabel.toLowerCase().includes('sleep');

        // If it's sleep data, calculate overall as sum of day and night where both exist
        let calculatedOverall = overall.values;
        if (isSleepData) {
            calculatedOverall = overall.values.map((_, index) => {
                const dayValue = index < day.values.length ? day.values[index] : 0;
                const nightValue = index < night.values.length ? night.values[index] : 0;
                
                // If both day and night have values, sum them up
                // Otherwise use the original overall value
                if (dayValue > 0 || nightValue > 0) {
                    return dayValue + nightValue;
                }
                return overall.values[index];
            });
        }
        
        // Use overall data dates as the common date axis
        return {
            overall: calculatedOverall,
            day: day.values,
            night: night.values,
            dates: overall.dates.length ? overall.dates : (day.dates.length ? day.dates : night.dates),
            isSleepData: isSleepData
        };
    }, [overallData, dayData, nightData, metricLabel]);

    // Determine if values are zero for coloring purposes
    const overallZeroValues = processedData.overall.map(value => value === 0);
    const dayZeroValues = processedData.day.map(value => value === 0);
    const nightZeroValues = processedData.night.map(value => value === 0);

    // Get colors based on viewType and zero values
    const getColorsForView = (values, zeroValues, viewType) => {
        // Get the right thresholds for this view
        const viewThresholds = viewType === 'day' && thresholds.day ? thresholds.day :
                            viewType === 'night' && thresholds.night ? thresholds.night :
                            viewType === 'overall' && thresholds.overall ? thresholds.overall :
                            thresholds;
                            
        return values.map((value, index) => 
            getBarColor(value, viewThresholds, zeroValues[index])
        );
    };
    
    // Get colors with the appropriate thresholds for each view
    const overallBarColors = getColorsForView(processedData.overall, overallZeroValues, 'overall');
    const dayBarColors = getColorsForView(processedData.day, dayZeroValues, 'day');
    const nightBarColors = getColorsForView(processedData.night, nightZeroValues, 'night');

    // Get active values based on viewType
    const activeValues = viewType === 'day' ? processedData.day : 
                        (viewType === 'night' ? processedData.night : processedData.overall);
    const activeBarColors = viewType === 'day' ? dayBarColors : 
                           (viewType === 'night' ? nightBarColors : overallBarColors);

    // Get the appropriate thresholds for the current view type
    const getViewSpecificThresholds = () => {
        // For respiratory rate in overall view, use SettingResp if available
        if (metricLabel === 'Respiratory Rate (bpm)' && viewType === 'overall' && SettingResp && SettingResp.normal_low !== undefined) {
            const normalLow = parseFloat(SettingResp.normal_low) || 12;
            const normalHigh = parseFloat(SettingResp.normal_high) || 20;
            const range = normalHigh - normalLow;
            
            return {
                low: Math.max(0, normalLow - range/2),
                normalLow: normalLow,
                average: (normalLow + normalHigh) / 2,
                normalHigh: normalHigh,
                high: normalHigh + range/2,
                status: 'normal'
            };
        }
        
        // Fall back to standard threshold calculation for other cases
        let result;
        if (viewType === 'day' && thresholds.day) {
            result = thresholds.day;
        } else if (viewType === 'night' && thresholds.night) {
            result = thresholds.night;
        } else if (viewType === 'overall' && thresholds.overall) {
            result = thresholds.overall;
        } else {
            result = thresholds;
        }
        
        // Ensure all threshold values are valid numbers
        const validatedResult = {
            low: Math.max(0, parseFloat(result.low) || 0),
            normalLow: Math.max(0, parseFloat(result.normalLow) || 0),
            average: Math.max(0, parseFloat(result.average) || 0),
            normalHigh: Math.max(0, parseFloat(result.normalHigh) || 100),
            high: Math.max(0, parseFloat(result.high) || 100),
            status: result.status || 'Unknown'
        };
        
        return validatedResult;
    };
    
    const viewSpecificThresholds = getViewSpecificThresholds();

    // Calculate full range for thresholds to ensure all zones are visible
    const getFullThresholdRange = () => {
        const stdDev = viewSpecificThresholds.high - viewSpecificThresholds.normalHigh;
        
        // Find actual min and max values in the data
        const maxDataValue = Math.max(...activeValues);
        const minDataValue = Math.min(...activeValues);
        
        // Calculate threshold-based min and max
        let maxThreshold = viewSpecificThresholds.high + stdDev;
        let minThreshold = Math.max(0, viewSpecificThresholds.low - stdDev); // Subtract stdDev from low, but keep >= 0
        
        // Adjust if data exceeds current thresholds (add buffer of 2-3 units)
        if (maxDataValue > maxThreshold) {
            maxThreshold = maxDataValue + 3;
        }
        
        if (minDataValue < minThreshold) {
            minThreshold = Math.max(0, minDataValue - 2); // Keep min at least 0
        }
        
        // For sleep data, cap at 24 hours (1440 minutes)
        if (processedData.isSleepData) {
            return {
                min: minThreshold,
                max: Math.min(maxThreshold, 1440)
            };
        }
        
        return {
            min: minThreshold,
            max: maxThreshold
        };
    };

    const thresholdRange = getFullThresholdRange();
    
    // Use threshold range directly for chart min/max
    const effectiveMinValue = minValue !== undefined ? minValue : thresholdRange.min;
    const effectiveMaxValue = maxValue !== undefined ? maxValue : thresholdRange.max;

    // Calculate gradient stops based on full range
    const getPercentage = (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 0;
        const range = effectiveMaxValue - effectiveMinValue;
        if (range === 0) return 50; // Avoid division by zero
        // Invert the percentage calculation to match chart orientation
        return Math.min(Math.max((100 - ((numValue - effectiveMinValue) / range * 100)), 0), 100).toFixed(1);
    };

    // Gradient goes from bottom to top to match chart orientation
    const gradientBackground = `linear-gradient(to top, 
        #FF8A8A 0% ${100 - getPercentage(viewSpecificThresholds.low)}%, 
        #FFE566 ${100 - getPercentage(viewSpecificThresholds.low)}% ${100 - getPercentage(viewSpecificThresholds.normalLow)}%, 
        #90FF8C ${100 - getPercentage(viewSpecificThresholds.normalLow)}% ${100 - getPercentage(viewSpecificThresholds.normalHigh)}%, 
        #FFE566 ${100 - getPercentage(viewSpecificThresholds.normalHigh)}% ${100 - getPercentage(viewSpecificThresholds.high)}%, 
        #FF8A8A ${100 - getPercentage(viewSpecificThresholds.high)}% 100%)`;

    // Function to parse time format like "10h 18m" into minutes
    const parseTimeToMinutes = (timeStr) => {
        if (!timeStr || timeStr === "0h 0m") return 0;
        
        const hourMatch = timeStr.match(/(\d+)h/);
        const minMatch = timeStr.match(/(\d+)m/);
        
        const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
        const minutes = minMatch ? parseInt(minMatch[1], 10) : 0;
        
        return hours * 60 + minutes;
    };

    // Ensure all threshold values are numbers and use view-specific values
    const validThresholds = React.useMemo(() => {
        // Use currentThresholds which is already view-specific
        let lowVal, normalLowVal, averageVal, normalHighVal, highVal;        
        // Check if this is sleep data and handle special parsing for time format
        if (metricLabel.toLowerCase().includes('sleep')) {
            // Parse time values if they're in string format (like "10h 18m")
            if (typeof viewSpecificThresholds.average === 'string') {
                averageVal = parseTimeToMinutes(viewSpecificThresholds.average);
            } else {
                averageVal = parseFloat(viewSpecificThresholds.average);
            }
            
            // Calculate other thresholds relative to the average
            const stdDev = parseFloat(viewSpecificThresholds.std_dev) || 240; // ~4 hours if not provided
            
            // Ensure we have a valid average, even if it's 0 for day view
            if (isNaN(averageVal)) {
                averageVal = 0;
            }
            
            // Generate threshold values based on average and standard deviation
            lowVal = Math.max(0, averageVal - (stdDev * 1.5));
            normalLowVal = Math.max(0, averageVal - stdDev);
            normalHighVal = Math.min(1440, averageVal + stdDev);  // Max 24 hours
            highVal = Math.min(1440, averageVal + (stdDev * 1.5));
            
            // For day view with zero sleep, ensure we have non-zero thresholds for display
            if (viewType === 'day' && averageVal === 0) {
                // Scale thresholds based on the other views
                const nightThresholds = thresholds.sleep_night || thresholds.night || {};
                const overallThresholds = thresholds.sleep_total || thresholds.overall || {};
                
                // Try to derive sensible values from night/overall if available
                const nightAvg = typeof nightThresholds.average === 'string' ? 
                    parseTimeToMinutes(nightThresholds.average) : 
                    parseFloat(nightThresholds.average) || 0;
                    
                const overallAvg = typeof overallThresholds.average === 'string' ? 
                    parseTimeToMinutes(overallThresholds.average) : 
                    parseFloat(overallThresholds.average) || 0;
                
                // Use a small fraction of night/overall sleep for day view thresholds
                if (nightAvg > 0 || overallAvg > 0) {
                    const reference = Math.max(nightAvg, overallAvg);
                    normalHighVal = Math.min(reference * 0.2, 120); // 20% of night sleep or 2 hours max
                    highVal = normalHighVal * 1.5;                  // 50% higher than normalHigh
                } else {
                    // Fallback if we really have no data
                    normalHighVal = 120; // 2 hours
                    highVal = 180;       // 3 hours
                }
            }
        } else {
            // Normal numeric parsing for non-sleep metrics
            lowVal = parseFloat(viewSpecificThresholds.low);
            normalLowVal = parseFloat(viewSpecificThresholds.normalLow);
            averageVal = parseFloat(viewSpecificThresholds.average);
            normalHighVal = parseFloat(viewSpecificThresholds.normalHigh);
            highVal = parseFloat(viewSpecificThresholds.high);
        }
        
        // Ensure thresholds are in ascending order
        return {
            low: Math.max(0, !isNaN(lowVal) ? lowVal : 0),
            normalLow: Math.max(0, !isNaN(normalLowVal) ? normalLowVal : 
                      Math.max(0, !isNaN(lowVal) ? lowVal * 1.2 : 0)),
            average: Math.max(0, !isNaN(averageVal) ? averageVal : 
                     Math.max(0, !isNaN(normalLowVal) ? normalLowVal * 1.5 : 0)),
            normalHigh: Math.max(0, !isNaN(normalHighVal) ? normalHighVal :
                       Math.max(0, !isNaN(averageVal) ? averageVal * 1.2 : getFullThresholdRange().max * 0.7)),
            high: Math.max(0, !isNaN(highVal) ? highVal : 
                  Math.max(0, !isNaN(normalHighVal) ? normalHighVal * 1.2 : getFullThresholdRange().max * 0.9)),
            status: viewSpecificThresholds.status || 'Unknown'
        };
    }, [viewType, viewSpecificThresholds, metricLabel]); // Re-calculate when these change

    // Sort threshold values to ensure they're in ascending order for display
    const sortedThresholds = React.useMemo(() => {
        // Ensure the thresholds are in ascending order for proper display
        const { low, normalLow, average, normalHigh, high, status } = validThresholds;
                
        return {
            low,
            normalLow,
            average,
            normalHigh,
            high,
            status
        };
    }, [validThresholds]); // Re-calculate when validThresholds change

    const calculateDailyStatuses = (historyData, validThresholds) => {
        // Check if we have valid data to process
        if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
            return ['No Data'];
        }

        const statusList = historyData.map(data => {
            // Handle null/undefined values or missing value property
            if (!data || data.value === null || data.value === undefined) {
                return 'No Data';
            }

            // Parse the value if it's a sleep time format (e.g., "8h 30m")
            let value = data.value;
            if (typeof value === 'string' && value.match(/^\d{1,2}h\s\d{1,2}m$/)) {
                value = parseTimeToMinutes(value);
            }

            // Evaluate against thresholds to determine status
            if (value <= validThresholds.low) return 'Caution';
            if (value <= validThresholds.normalLow) return 'Attention';
            if (value <= validThresholds.normalHigh) return 'Good';
            if (value <= validThresholds.high) return 'Attention';
            return 'Caution';
        });
        return statusList;
    }

    let activeData;
    switch(viewType) {
        case 'day':
            activeData = dayData;
            break;
        case 'night':
            activeData = nightData;
            break;
        case 'overall':
        default:
            activeData = overallData;
            break;
    }
    
    const statusList = calculateDailyStatuses(activeData, validThresholds);

    const calculateWeeklyStatus = (statusList) => {
        // If we have no status list or it's empty, return 'No Data'
        if (!statusList || statusList.length === 0) {
            return 'No Data';
        }
        
        // If there is not enough data, return 'Insufficient data'
        const noDataCount = statusList.filter(status => status === 'No Data').length;
        if (noDataCount >= 3 || statusList.length < 3) return 'Insufficient data';

        // Calculate weekly status based on the most common status of the week
        const weeklyStatusCounts = statusList.reduce((acc, status) => {
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        
        // If all counts are equal, prioritize in this order: Good > Attention > Caution
        const statusPriority = { 'Good': 3, 'Attention': 2, 'Caution': 1, 'No Data': 0 };
        
        const mostCommonStatus = Object.keys(weeklyStatusCounts).reduce((a, b) => {
            // If counts are the same, choose the one with higher priority
            if (weeklyStatusCounts[a] === weeklyStatusCounts[b]) {
                return statusPriority[a] > statusPriority[b] ? a : b;
            }
            // Otherwise choose the one with higher count
            return weeklyStatusCounts[a] > weeklyStatusCounts[b] ? a : b;
        });
        
        return mostCommonStatus;
    };

    const weeklyStatus = calculateWeeklyStatus(statusList);

    // Format minutes into hours and minutes or add RR unit for respiratory rate
    const formatDuration = (minutes) => {
        if (minutes === 0 || minutes === null) return '';
        
        if (metricLabel.includes('Respiratory Rate')) {
            const value = typeof minutes === 'number' ? minutes.toFixed(1) : '0.0';
            return `${value} bpm`;
        }
        
        if (metricLabel.includes('Sleep')) {
            // Cap sleep duration at 24 hours (1440 minutes)
            const cappedMinutes = Math.min(minutes, 1440);
            const hours = Math.floor(cappedMinutes / 60);
            const mins = Math.round(cappedMinutes % 60);
            return `${hours}h ${mins}m`;
        }
        
        return typeof minutes === 'number' ? minutes.toFixed(1) : '0.0';
    };

    const formatLabel = (value) => {
        if (typeof value === 'object' && value.value && value.unit) {
            return (
                <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                    <span>{value.value}</span>
                    <span style={{ fontSize: '0.7em', marginLeft: '2px', marginTop: '-2px' }}>{value.unit}</span>
                </div>
            );
        }
        return value;
    };

    // Process data to create datasets for Chart.js
    const createDatasets = () => {
        // Use the original data without any zero replacements
        if (viewType === 'overall') {
            if (processedData.isSleepData) {
                // For sleep data in overall view - show stacked bars (night + day) with trend line
                return [
                    {
                        type: "line",
                        label: "Trend",
                        data: processedData.overall,
                        borderColor: "#3498db",
                        borderWidth: 4,
                        tension: 0.4,
                        fill: false,
                        pointBackgroundColor: "#3498db",
                        pointRadius: 4,
                        order: 1,
                        spanGaps: false,
                        datalabels: {
                            display: false
                        },
                        pointStyle: 'line',
                        pointBorderWidth: 2
                    },
                    {
                        label: "Overall" + metricLabel,
                        data: processedData.overall,
                        type: "bar",
                        backgroundColor: 'rgba(200, 200, 200, 0.7)', // Light gray with some transparency
                        borderColor: '#4B4B4D',
                        borderWidth: 1,
                        order: 2,
                        legendPointStyle: 'rect',
                        legendBorderColor: 'black',
                        legendBackgroundColor: 'transparent'
                    },
                    // {
                    //     data: processedData.night.map((value, index) => value === 0 ? null : value),
                    //     type: "bar",
                    //     backgroundColor: overallBarColors.map((color, index) => 
                    //         processedData.night[index] === 0 ? 'rgba(0,0,0,0)' : color.replace(')', ', 0.7)')
                    //     ),
                    //     borderColor: '#071170',
                    //     borderWidth: 5,
                    //     stack: 'Stack 0',
                    //     order: 2,
                    //     legendPointStyle: 'rect',
                    //     legendBorderColor: 'black',
                    //     legendBackgroundColor: 'transparent'
                    // },
                    // {
                    //     label: "Day " + metricLabel,
                    //     data: processedData.day.map((value, index) => value === 0 ? null : value),
                    //     type: "bar",
                    //     backgroundColor: overallBarColors.map((color, index) => 
                    //         processedData.day[index] === 0 ? 'rgba(0,0,0,0)' : color.replace(')', ', 0.7)')
                    //     ),
                    //     borderColor: '#FFF023',
                    //     borderWidth: 5,
                    //     stack: 'Stack 0',
                    //     order: 3,
                    //     legendPointStyle: 'rect',
                    //     legendBorderColor: 'black',
                    //     legendBackgroundColor: 'transparent'
                    // }
                ];
            } else {
                // For respiratory rate - just show the overall value with trend line
                return [
                    {
                        type: "line",
                        label: "Trend",
                        data: processedData.overall,
                        borderColor: "#3498db",
                        borderWidth: 4,
                        tension: 0.4,
                        fill: false,
                        pointBackgroundColor: "#3498db",
                        pointRadius: 4,
                        order: 1,
                        spanGaps: false,
                        datalabels: {
                            display: false
                        },
                        pointStyle: 'line',
                        pointBorderWidth: 2
                    },
                    {
                        label: "Overall " + metricLabel,
                        data: processedData.overall,
                        type: "bar",
                        backgroundColor: 'rgba(200, 200, 200, 0.7)', // Light gray with some transparency
                        borderColor: "#4B4B4D",
                        borderWidth: 1,
                        order: 2,
                        legendPointStyle: 'rect',
                        legendBorderColor: 'black',
                        legendBackgroundColor: 'transparent'
                    }
                ];
            }
        } else {
            // Day or Night view - show the single selected value with trend line
            return [
                {
                    type: "line",
                    label: "Trend",
                    data: processedData[viewType],
                    borderColor: "#3498db",
                    borderWidth: 4,
                    tension: 0.4,
                    fill: false,
                    pointBackgroundColor: "#3498db",
                    pointRadius: 4,
                    order: 1,
                    spanGaps: false,
                    datalabels: {
                        display: false
                    },
                    pointStyle: 'line',
                    pointBorderWidth: 2
                },
                {
                    label: viewType.charAt(0).toUpperCase() + viewType.slice(1) + " " + metricLabel,
                    data: activeValues,
                    type: "bar",
                    backgroundColor: 'rgba(200, 200, 200, 0.7)', // Light gray with some transparency
                    borderColor: '#4B4B4D',
                    borderWidth: 1,
                    order: 2,
                    legendPointStyle: 'rect',
                    legendBorderColor: 'black',
                    legendBackgroundColor: 'transparent',
                    datalabels: {
                        display: true,
                        color: 'black',
                        anchor: 'end',
                        align: 'top',
                        offset: -2,
                        formatter: (value) => {
                            return value > 0 ? formatDuration(value) : '';
                        },
                        font: {
                            weight: 'bold',
                            size: 11
                        }
                    }
                }
            ];
        }
    };

    const data = {
        labels: processedData.dates,
        datasets: createDatasets()
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 45,
                top: 30,
                right: 20,
                bottom: 10
            }
        },
        scales: {
            y: {
                beginAtZero: true, // Always start at zero
                min: 0, // Explicitly set minimum to 0
                max: getFullThresholdRange().max * 1.1, // Add some padding to the max
                stacked: viewType === 'overall' && processedData.isSleepData,
                grid: {
                    display: true
                },
                title: {
                    display: true,
                    text: metricLabel.includes('Respiratory Rate') ? 'Respiratory Rate (bpm)' :
                          metricLabel.includes('Sleep') ? 'Total Sleep (hours&mins)' :
                          metricLabel.includes('Bed Exit') ? 'Bed Exits' : '',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        // Add threshold labels with proper formatting
                        const thresholdValue = [
                            viewSpecificThresholds.low,
                            viewSpecificThresholds.normalLow,
                            viewSpecificThresholds.normalHigh,
                            viewSpecificThresholds.high
                        ];
                        
                        // For threshold values, add a small indicator
                        if (thresholdValue.includes(value)) {
                            return `→ ${formatDuration(Math.max(0, value))}`;
                        }
                        
                        return formatDuration(Math.max(0, value));
                    },
                    // Add key threshold values to ensure they appear on the axis
                    includeBounds: true,
                    // Force include the threshold values
                    afterBuildTicks: function(scale) {
                        const originalTicks = scale.ticks;
                        
                        // Add our threshold values
                        const thresholdsTicks = [
                            { value: 0 }, // Always show zero
                            { value: viewSpecificThresholds.low },
                            { value: viewSpecificThresholds.normalLow },
                            { value: viewSpecificThresholds.normalHigh },
                            { value: viewSpecificThresholds.high }
                        ];
                        
                        // Combine with existing ticks, filter duplicates and sort
                        const combinedTicks = [...originalTicks, ...thresholdsTicks];
                        const uniqueTicks = Array.from(new Map(combinedTicks.map(tick => 
                            [tick.value, tick])).values());
                        
                        // Sort by value
                        uniqueTicks.sort((a, b) => a.value - b.value);
                        
                        return uniqueTicks;
                    }
                }
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Daily Reports',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    font: {
                        size: 11
                    },
                    padding: 10
                }
            }
        },
        plugins: {
            // Important: Use a function here to pass the plugin with access to component state
            // This allows the plugin to access the current viewSpecificThresholds
            thresholds: viewSpecificThresholds,
            annotation: {
                annotations: {
                    // lowLine: {
                    //     type: 'line',
                    //     yMin: viewSpecificThresholds.low,
                    //     yMax: viewSpecificThresholds.low,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalLowLine: {
                    //     type: 'line',
                    //     yMin: viewSpecificThresholds.normalLow,
                    //     yMax: viewSpecificThresholds.normalLow,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalHighLine: {
                    //     type: 'line',
                    //     yMin: viewSpecificThresholds.normalHigh,
                    //     yMax: viewSpecificThresholds.normalHigh,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything    
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // highLine: {
                    //     type: 'line',
                    //     yMin: viewSpecificThresholds.high,
                    //     yMax: viewSpecificThresholds.high,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // }
                }
            },
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: function(value) {
                    return value > 0 ? formatDuration(value) : '';
                },
                font: {
                    weight: 'bold',
                    size: 11
                }
            },
            legend: {
                display: true,
                position: 'top',
                align: 'center',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Overall Respiratory Rate (bpm)': 'Overall Respiratory Rate',
                        'Day Sleep Respiratory Rate (bpm)': 'Day Sleep Respiratory Rate',
                        'Night Sleep Respiratory Rate (bpm)': 'Night Sleep Respiratory Rate',
                        'OverallTotal Sleep (Hours&Minutes)': 'Overall Total Sleep',
                        'Day Total Sleep (Hours&Minutes)': 'Day Total Sleep',
                        'Night Total Sleep (Hours&Minutes)': 'Night Total Sleep',
                        'Trend': 'Trend of the metric'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Debug: Log the exact legend item text to see what we're working with
                    console.log('Legend item text:', JSON.stringify(legendItem.text));
                    console.log('Available tooltip keys:', Object.keys(tooltips));
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    } else {
                        // Fallback: Try to find a close match for the text
                        const keys = Object.keys(tooltips);
                        const closeMatch = keys.find(key => 
                            key.toLowerCase().includes('night') && 
                            key.toLowerCase().includes('sleep'));
                        
                        if (closeMatch) {
                            tooltipEl.innerHTML = tooltips[closeMatch];
                            tooltipEl.style.opacity = '1';
                            tooltipEl.style.left = event.native.pageX + 'px';
                            tooltipEl.style.top = (event.native.pageY - 28) + 'px';
                            
                            // Log that we're using a close match
                            console.log('Using close match:', closeMatch, 'for', legendItem.text);
                        }
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                },
                labels: {
                    boxWidth: 30,
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: true,
                    font: {
                        size: 12,
                        weight: '500'
                    },
                    color: '#444',
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: i === 0 ? dataset.borderColor : 'transparent',
                            strokeStyle: i === 0 ? dataset.borderColor : 'black',
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            pointStyle: i === 0 ? dataset.pointStyle : dataset.legendPointStyle,
                            borderColor: i === 0 ? dataset.borderColor : dataset.legendBorderColor,
                            backgroundColor: i === 0 ? dataset.borderColor : dataset.legendBackgroundColor
                        }));
                    }
                }
            },
            title: {
                display: true,
                text: [title],
                font: {
                    size: 16,
                    weight: 'bold'
                }
            }
        }
    };

    // Add custom datalabels to display time in human-readable format for sleep data
    if (processedData.isSleepData) {
        options.plugins.datalabels = {
            color: '#444',
            font: {
                weight: 'bold'
            },
            formatter: function(value, context) {
                // Skip null values (previously 0s that we're hiding)
                if (value === null) return '';
                
                // For sleep data, format as hours and minutes
                if (processedData.isSleepData) {
                    const hours = Math.floor(value / 60);
                    const minutes = Math.floor(value % 60);
                    return `${hours}h ${minutes}m`;
                }
                return value;
            }
        };
    }

    const handleViewTypeChange = (newViewType) => {        // Update state with new view type
        setViewType(newViewType);
    };

    return (
        <div style={{ position: 'relative', height: '600px', width: '100%', display: 'flex', flexDirection: 'column', marginTop: '80px', marginBottom: '80px' }}>
            {/* View Type Selector */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                <button
                    onClick={() => handleViewTypeChange('overall')}
                    style={{
                        padding: '5px 15px',
                        backgroundColor: viewType === 'overall' ? '#4a90e2' : '#f0f0f0',
                        color: viewType === 'overall' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        marginRight: '5px',
                        cursor: 'pointer'
                    }}
                >
                    Overall
                </button>
                <button
                    onClick={() => handleViewTypeChange('day')}
                    style={{
                        padding: '5px 15px',
                        backgroundColor: viewType === 'day' ? '#4a90e2' : '#f0f0f0',
                        color: viewType === 'day' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        marginRight: '5px',
                        cursor: 'pointer'
                    }}
                >
                    Day Sleep
                </button>
                <button
                    onClick={() => handleViewTypeChange('night')}
                    style={{
                        padding: '5px 15px',
                        backgroundColor: viewType === 'night' ? '#4a90e2' : '#f0f0f0',
                        color: viewType === 'night' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Night Sleep
                </button>
            </div>
            
            <div style={{ flex: 1, height: '600px', width: '100%', justifyContent: 'left' }}>
                <Chart data={data} options={options} plugins={[thresholdBackgroundPlugin]} />
            </div>
        </div>
    );
};

export default ModularTrendsTimeSplitChart;
